import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { storage } from '@uhg-abyss/web/tools/storage';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { useLagoon } from '../../hooks/useLagoon';
import { useAnalyticsStore } from '../../store/useAnalyticsStore';
import { AnalyticsStore } from '../../store/useAnalyticsStore/analyticsStore';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { usePCPStore } from '../../store/usePCPStore';
import { PCPSearchStore } from '../../store/usePCPStore/pcpStore';
import { useSearchStore } from '../../store/useSearchStore';
import { SearchState } from '../../store/useSearchStore/searchStore';
import { useTypeaheadStore } from '../../store/useTypeaheadStore';
import { TypeaheadState } from '../../store/useTypeaheadStore/typeaheadStore';
import { handleOnCommonlySearchedClick } from '../../utils/typeahead.utils';
import {
  getCoverageTypes,
  getCurrentMember,
  getMemberCoverageTypeSitusState,
  getOnlineRetailers,
  useIsEligibleForStandaloneDental,
} from '../../utils/user.utils';
import { adobeLinkTrackEvent } from '../AdobeTagging/adobeLinkTrackEvent';
import { CardHeader } from '../CardHeader/CardHeader';
import { Chip } from '../Chip';
import {
  Constants,
  MemberCoverageTypeToUICoverageTypeConversion,
} from '../Constants';
import { FeatureFlags, SECTION_TERM } from '../ConstantsFeatureFlags';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { cardHeaderNullPageStyle } from '../ConstantsStyles';
import { ContentWrapper } from '../ContentWrapper';
import { ContainerForAllSectionsStyled } from '../ProviderDetailTabs/ProviderDetailTabs.styled';
import { getGeoLocationFromStorage } from '../PSXHeader/SearchBar/utils';
import { mappingRollUpCodeToCategory } from '../RecentActivity/helper';
import { getFeatureFlag } from '../Utils';
import { replaceSpaceToUpperCase } from '../Utils/replaceString';
import { CommonlySearchedDental } from './CommonlySearchedDental';

export const ChipContainer = styled(Flex, {
  gap: '8px',
  '@media (max-width: 744px)': {
    gap: '12px',
  },
});

export interface CommonSearch {
  id: string;
  category?: string;
  icon: string;
  key: string;
  score?: string;
  psxKeyword: string;
  translatedPsxKeyword: string;
  searchType: string;
  dentalTerm: string;
  visionTerm: string;
  bhTerm: string;
  path?: string;
  embeddedUrl?: string;
  specialitySet?: string;
  DNT_dentalTerm?: string;
  DNT_visionTerm?: string;
  coverageType?: string | undefined;
}

export type Props = {
  selectedChipValue?: string;
  onCommonSearchChipClicked?: () => void;
};

const { ALL, BEHAVIORAL_HEALTH, DENTAL, VISION } = Constants.CHIPS_CATEGORIES;

const createVision = (
  visionTerm: string,
  path: string,
  lat: string,
  long: string,
  baseUrl: string,
  currentZipcode: string
): URL => {
  let visionUrl: URL;
  if (path.includes('rp_zipcode')) {
    visionUrl = new URL(
      baseUrl + path?.replace('rp_zipcode', currentZipcode || '')
    );
    visionUrl.searchParams.set('lat', lat);
    visionUrl.searchParams.set('long', long);
  } else {
    visionUrl = new URL(baseUrl + path);
  }

  return visionUrl;
};

export const CommonlySearchedCard = ({
  selectedChipValue = ALL,
  onCommonSearchChipClicked,
}: Props) => {
  const { t } = useTranslation();

  const { longitude, latitude } = getGeoLocationFromStorage();

  const { ZIPCODE } = Constants.STORAGE_KEYS.LOCAL;
  const currentZipcode = storage.local.get(ZIPCODE);

  const baseUrlLagoon = useLagoon(
    Constants.LAGOON_TABLE.VISION_AND_DENTAL_REDIRECTION_BASEURL
  )();
  const baseUrl = baseUrlLagoon?.[0]?.baseUrl;

  const COMMONLY_SEARCHED =
    FeatureFlags.FEATURE_FLAG_KEY_CHIP_MAP[selectedChipValue][
      SECTION_TERM.COMMONLY_SEARCHED
    ];
  const featureFlags = useLagoon('feature-flags')();
  const showCommonlySearched = getFeatureFlag(featureFlags, COMMONLY_SEARCHED);
  const enableCns = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_CNS
  );

  const enableRetriveTypeAheadDataFlag = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_RETRIVE_TYPEAHEAD_DATA
  );

  const enableSearchResultsV2 = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_SEARCH_RESULTS_V2
  );

  const enableRetriveTypeAheadData =
    enableRetriveTypeAheadDataFlag && enableSearchResultsV2;

  const lagoonTableMapping = {
    [BEHAVIORAL_HEALTH]: Constants.LAGOON_TABLE.COMMONLY_SEARCHED.BH_NULL_PAGE,
    [VISION]: Constants.LAGOON_TABLE.COMMONLY_SEARCHED.VISION_NULL_PAGE,
    [DENTAL]: Constants.LAGOON_TABLE.COMMONLY_SEARCHED.DENTAL_NULL_PAGE,
  };
  const currentMember = getCurrentMember();
  const memberCoverages: [] = getCoverageTypes(currentMember);
  const convertedMemberCoverages = memberCoverages?.map(
    (cov) => MemberCoverageTypeToUICoverageTypeConversion[cov]
  );
  const isCns = enableCns && currentMember?.isCnsMember;

  const comonSearchesLagoonTable = isCns
    ? 'browse-for-care'
    : lagoonTableMapping[selectedChipValue] ||
      Constants.LAGOON_TABLE.COMMONLY_SEARCHED[selectedChipValue];

  const commonSearchesData: CommonSearch[] =
    comonSearchesLagoonTable && useLagoon(comonSearchesLagoonTable)();
  const { navigate } = useRouter();

  const { setTypeaheadSearchStore, keyword = '' } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      keyword: state.typeaheadSearchStore.keyword,
      setTypeaheadSearchStore: state.setTypeaheadSearchStore,
    }))
  );
  const chipStore = useChipStore(
    useShallow((state: ChipState) => ({
      setChipValue: state.setChipValue,
      setCoverageType: state.setCoverageType,
    }))
  );
  const { setAnalyticsState } = useAnalyticsStore(
    useShallow((state: AnalyticsStore) => ({
      setAnalyticsState: state.setAnalyticsState,
    }))
  );
  const { setPCPSearchState } = usePCPStore(
    useShallow((state: PCPSearchStore) => ({
      setPCPSearchState: state.setPCPSearchState,
    }))
  );
  const { setSearchStore } = useSearchStore(
    useShallow((state: SearchState) => ({
      setSearchStore: state.setSearchStore,
    }))
  );

  const isEligibleForStandaloneDental = useIsEligibleForStandaloneDental();

  const getUpdatedToken = (item, comonSearchesLagoonTable: string) => {
    adobeLinkTrackEvent({
      name: item?.psxKeyword,
      location: 'body:browse for care',
      type: 'internal',
    });
    const hasKeyWorkIncluded = [item?.psxKeyword || item?.searchTerm].includes(
      keyword
    );

    if (hasKeyWorkIncluded) {
      return;
    }

    onCommonSearchChipClicked?.();

    handleOnCommonlySearchedClick({
      item,
      psxSource: comonSearchesLagoonTable,
      navigate,
      chipStore,
      setAnalyticsState,
      setPCPSearchState,
      setTypeaheadSearchStore,
      setSearchStore,
      getOnlineRetailers,
      mappingRollUpCodeToCategory,
      enableRetriveTypeAheadData,
      isEligibleForStandaloneDental,
    });
  };
  const getExternalUrlAndOpen = (item) => {
    adobeLinkTrackEvent({
      name: item?.psxKeyword,
      location: 'body:browse for care',
      type: 'external',
    });
    const situsState: string = getMemberCoverageTypeSitusState(currentMember);
    const externalUrl =
      situsState === Constants.MEMBER_COVERAGE_TYPE_SITUSSTATE
        ? Constants.CNS_VISION_URL.ARIZONA
        : Constants.CNS_VISION_URL.DEFAULT;
    window?.open(externalUrl, '_blank');
  };
  const getVisionCare = () =>
    commonSearchesData?.map((item) => {
      const { visionTerm, id, icon, DNT_visionTerm, psxKeyword } = item;
      const { path = '' } = item;
      const visionUrl: URL = createVision(
        visionTerm,
        path,
        latitude,
        longitude,
        baseUrl,
        currentZipcode
      );
      return visionTerm ? (
        <Chip
          adobeChipName={DNT_visionTerm}
          data-auto-testid={`vision-searched-${replaceSpaceToUpperCase(
            visionTerm
          )}`}
          data-testid={`vision-searched-${replaceSpaceToUpperCase(visionTerm)}`}
          icon={icon}
          isStandardAnchor={false}
          key={`vision-search-${id}`}
          text={visionTerm}
          url={visionUrl.toString()}
        />
      ) : (
        <Chip
          adobeChipName={DNT_visionTerm}
          data-auto-testid={`vision-searched-${replaceSpaceToUpperCase(
            psxKeyword
          )}`}
          data-testid={`vision-searched-${replaceSpaceToUpperCase(psxKeyword)}`}
          icon={icon}
          key={`vision-search-${id}`}
          onClick={() => getUpdatedToken(item, comonSearchesLagoonTable)}
          text={psxKeyword}
        />
      );
    });
  const getBrowseForCare = (chipValue) => {
    switch (chipValue) {
      case ALL:
        return (
          <ChipContainer aria-labelledby="browse-for-care" role="group">
            {commonSearchesData?.map((item) => {
              const {
                psxKeyword,
                translatedPsxKeyword,
                id,
                icon,
                coverageType = '',
              } = item;
              const isCNSOptometrist =
                isCns &&
                psxKeyword === Constants.ALL_PAGE_BROWSECARE_PSXKEYWORD;
              // The logic of whether to show or hide the chip based on coverage type is applicable for all lines of business (LOBs).
              const toShowChip = (
                convertedMemberCoverages as string[]
              )?.includes(coverageType);

              const iconMapping: Record<string, string> = {
                mental_health: 'commonSearchMentalHealth',
              };

              const mappedValue = iconMapping[icon] ? iconMapping[icon] : icon;
              return (
                <Chip
                  data-auto-testid={`commonly-searched-${replaceSpaceToUpperCase(
                    psxKeyword
                  )}`}
                  data-testid={`commonly-searched-${replaceSpaceToUpperCase(
                    psxKeyword
                  )}`}
                  extraIcon={isCNSOptometrist}
                  hide={!toShowChip}
                  icon={mappedValue}
                  key={`common-search-${id}`}
                  onClick={() => {
                    isCNSOptometrist
                      ? getExternalUrlAndOpen(item)
                      : getUpdatedToken(item, comonSearchesLagoonTable);
                  }}
                  text={translatedPsxKeyword}
                />
              );
            })}
          </ChipContainer>
        );
      case DENTAL:
        return (
          <ChipContainer>
            {commonSearchesData?.map((item) => {
              let { path } = item;
              const { dentalTerm, id, icon, DNT_dentalTerm, psxKeyword } = item;
              path = path?.replace('rp_zipcode', currentZipcode || '');
              const url = new URL(`${baseUrl}${path}`);
              url.searchParams.set('long', longitude);
              url.searchParams.set('lat', latitude);
              return (
                <CommonlySearchedDental
                  DNT_dentalTerm={DNT_dentalTerm}
                  dentalTerm={dentalTerm}
                  icon={icon}
                  id={id}
                  item={item}
                  key={`common-dental-${id}`}
                  psxKeyword={psxKeyword}
                  updatedToken={() =>
                    getUpdatedToken(item, comonSearchesLagoonTable)
                  }
                  url={url.toString()}
                />
              );
            })}
          </ChipContainer>
        );
      case VISION:
        return <ChipContainer>{getVisionCare()}</ChipContainer>;

      case BEHAVIORAL_HEALTH:
        return (
          <ChipContainer>
            {commonSearchesData?.map((item) => {
              const { psxKeyword, id, icon } = item;
              return (
                <Chip
                  data-auto-testid={`bh-searched-${replaceSpaceToUpperCase(
                    psxKeyword
                  )}`}
                  data-testid={`bh-searched-${replaceSpaceToUpperCase(
                    psxKeyword
                  )}`}
                  icon={icon}
                  key={`bh-search-${id}`}
                  onClick={() =>
                    getUpdatedToken(item, comonSearchesLagoonTable)
                  }
                  text={psxKeyword}
                />
              );
            })}
          </ChipContainer>
        );
      default:
        return null;
    }
  };

  const browseForCare = getBrowseForCare(selectedChipValue);

  return showCommonlySearched && commonSearchesData && browseForCare ? (
    <ContainerForAllSectionsStyled height="auto">
      <ContentWrapper>
        <CardHeader
          css={cardHeaderNullPageStyle}
          data-auto-testid="commonly-searched-card-heading"
          data-testid="commonly-searched-card-heading"
          id="browse-for-care"
          level={2}
        >
          {t('Browse for care')}
        </CardHeader>
        {browseForCare}
      </ContentWrapper>
    </ContainerForAllSectionsStyled>
  ) : null;
};
